import { createTheme } from '@mui/material/styles';

const PICKER_INK = '#101828';

// Single source of truth for the gap between a dialog and the viewport edge,
// and for the dialog's corner radius. Applied to every MUI Dialog via the
// MuiDialog override below, and to Modal-based popups via ModalComponent —
// so individual popups must not set their own paper margin/width/radius.
export const DIALOG_MARGIN = 8;
export const DIALOG_RADIUS = 20;

// Below this width a dialog/modal fills the screen edge to edge, less the gutter.
export const DIALOG_MOBILE_BREAKPOINT = 600;

// The full available width once the gutter is taken off both sides.
export const DIALOG_WIDTH = `calc(100% - ${DIALOG_MARGIN * 2}px)`;

// On phones every dialog is full-bleed: the maxWidth cap is lifted so the paper
// stretches to DIALOG_WIDTH instead of shrinking to its content or its cap.
const MOBILE_FULL_WIDTH = {
  [`@media (max-width:${DIALOG_MOBILE_BREAKPOINT - 0.05}px)`]: {
    width: DIALOG_WIDTH,
    maxWidth: DIALOG_WIDTH,
    margin: DIALOG_MARGIN,
  },
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1024,
      lg: 1200,
      xl: 1440,
      xxl: 1920,
    },
  },
  palette: {
    primary: {
      main: "#e6eaed"
    },
    secondary: {
      main: "#e9f5f8"
    },
    success: {
      main: '#eaf6ef'
    },
    warning: {
      main: "#fff8e6"
    },
    error: {
      main: "#fbe9e9"
    },
    customColors: {
      white: "#FFFFFF",
      yellow_bg: "#ffffff",
      purple_dark: "#6c3bc2",
      b50: '#ebe8fb',
      b75: '#ada2ed',
      b100: '#8c7ce6',
      b200: '#5a43db',
      b300: 'black',
      b400: '#271494',
      b500: '#221281',
      b600: 'black',
      //indigo25: '#F5F8FF',
      indigo25: '#FFFDFA',
      indigo50: '#EEF4FF',
      indigo100: '#E0EAFF',
      indigo600: 'black',
      indigo700: "black",
      r50: '#feeded',
      r75: '#fdb7b7',
      r100: '#fc9999',
      r200: '#fa6d6d',
      r300: '#f94f4f',
      r400: '#ae3737',
      r500: '#983030',
      y25: '#ffe9a3',
      y50: '#fffae6',
      y75: '#ffe996',
      y100: '#ffe06b',
      y200: '#ffd22b',
      y300: '#ffc900',
      y400: '#b38d00',
      y500: '#9c7b00',
      g50: '#e8e9ea',
      g75: '#a1a3a7',
      g100: '#7a7d83',
      g200: '#40454d',
      g300: '#191f29',
      g400: '#12161d',
      g500: '#0f1319',
      g700: '#344054',
      g900: '#101828',
      gray200: "#EAECF0",
      gray300: "#EAECEF",
      whiteBG: '#ffffff',
      inputField: '#f6f6f6',
      disable: '#e7e7e7',
      information: '#e6f1f8',
      tableHeader: "#eae8fb",
      // kept for investor-web: CustomCheckBox and PitchDeckImportPopup read this
      checkboxcolor: "#7c4dff"
    },
  },
  background: {
    paper: "#FFFFFF"
  },
  typography: {
    // fontFamily: '"DM Sans", sans-serif', // Default font
    title: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Specific font for title
    },
    titleNormal: {
      fontSize: "1.75rem",
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif',
    },
    h1: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Specific font for h1
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Specific font for h2
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Specific font for h3
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Use "DM Sans" for body text
    },
    bodyTextBold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important' // Use "Lato" for bold body text
    },
    bodyTextMedium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important' // Use "Lato" for medium body text
    },
    bodyTextRegular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important' // Use "Lato" for regular body text
    },
    label1Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif', // Use "DM Sans" for label bold
    },
    label1Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important', // Use "Lato" for regular labels
    },
    label2Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label2Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label2Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label3Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label3Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label4Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label4Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label4Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    label4Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption1Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption1Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption1Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption2Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption2Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    caption2Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action1Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action1Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action1Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action2Bold: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action2Medium: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    action2Regular: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    p: {
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
    span: {
      lineHeight: '1.3 !important',
      fontFamily: '"Lato", sans-serif !important'
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          lineHeight: '1.3 !important',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
          '& .MuiFilledInput-root': {
            backgroundColor: '#ffffff',
            border: '1px solid #A1A3A7',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#ffffff',
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              borderColor: '#A1A3A7',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#7A7D83',
            opacity: 1,
          },
          '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        // MUI defaults to a 32px gutter; DIALOG_MARGIN is the single knob for it.
        // paperFullScreen resolves after paper, so full-screen dialogs keep margin: 0.
        paper: {
          margin: DIALOG_MARGIN,
          maxHeight: `calc(100% - ${DIALOG_MARGIN * 2}px)`,
          borderRadius: DIALOG_RADIUS,
          ...MOBILE_FULL_WIDTH,
        },
        // The paperWidth* slots resolve after `paper` and carry the maxWidth cap
        // for each `maxWidth` prop, so the mobile rule has to be repeated here to
        // beat them — otherwise a maxWidth="xs" dialog stays narrow on a phone.
        paperWidthXs: MOBILE_FULL_WIDTH,
        paperWidthSm: MOBILE_FULL_WIDTH,
        paperWidthMd: MOBILE_FULL_WIDTH,
        paperWidthLg: MOBILE_FULL_WIDTH,
        paperWidthXl: MOBILE_FULL_WIDTH,
        paperWidthFalse: MOBILE_FULL_WIDTH,
        paperFullWidth: {
          width: DIALOG_WIDTH,
        },
      },
    },
    // palette.primary.main is a near-white grey, so the pickers' selected day,
    // toolbar and action bar inherit it and become unreadable. Pin them to ink.
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.MuiPickersDay-today': {
            borderColor: PICKER_INK,
          },
          '&.Mui-selected': {
            backgroundColor: PICKER_INK,
            color: '#FFFFFF',
            '&:hover, &:focus': {
              backgroundColor: PICKER_INK,
              color: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': {
            backgroundColor: PICKER_INK,
            color: '#FFFFFF',
            '&:hover, &:focus': {
              backgroundColor: PICKER_INK,
              color: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          '&.Mui-selected': {
            backgroundColor: PICKER_INK,
            color: '#FFFFFF',
            '&:hover, &:focus': {
              backgroundColor: PICKER_INK,
              color: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          color: PICKER_INK,
        },
      },
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: {
          color: '#7a7d83',
          '&.Mui-selected': {
            color: PICKER_INK,
          },
        },
      },
    },
    // The action bar renders plain MUI Buttons, so they take colour from
    // primary.main. Target them by class — PickersActionBar is not a styled
    // component, so a `styleOverrides.actionBar` key would never reach it.
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-actionBar .MuiButton-root': {
            color: PICKER_INK,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(16, 24, 40, 0.06)',
            },
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '&.MuiPickersLayout-actionBar .MuiButton-root': {
            color: PICKER_INK,
            fontWeight: 600,
          },
        },
      },
    },
  },
});


export default theme;
